import { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import CommonLoading from "../components/loader/CommonLoading";
import { getCommunityAction, getJoinedCommunitiesAction, getComMembersAction, joinCommunityAction, leaveCommunityAction } from "../redux/actions/communityActions";
import { getComPostsAction } from "../redux/actions/postActions";
import { getFollowingUsersAction, followUserAction, unfollowUserAction } from "../redux/actions/userActions";
import CommunityMainSection from "../components/community/MainSection";
import { FaFileAlt, FaInfoCircle, FaUserPlus, FaUserMinus, FaSignOutAlt } from "react-icons/fa";
import { IoMdPeople } from "react-icons/io";
import placeholder from "../assets/placeholder.png";
import { RichTextContent } from "../utils/richTextProcessor";
import { useToast } from "../contexts/ToastContext";

const MyCommunityDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { communityName } = useParams();
  const { success: showSuccess, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState("about");
  const [loading, setLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [initialTabResolved, setInitialTabResolved] = useState(false);
  const [initialPostsRequested, setInitialPostsRequested] = useState(false);

  const { communityData, joinedCommunities, communityMembers } = useSelector((state) => state.community || {});
  const totalCommunityPosts = useSelector((state) => state.posts?.totalCommunityPosts || 0);
  const authUser = useSelector((state) => state.auth?.userData);
  
  const isMember = joinedCommunities?.some(
    ({ name }) => name === communityName
  );
  const followingUsers = useSelector((state) => state.user?.followingUsers);

  useEffect(() => {
    if (communityName && communityName !== 'undefined') {
      dispatch(getCommunityAction(communityName));
      dispatch(getJoinedCommunitiesAction());
      dispatch(getComMembersAction(communityName));
      dispatch(getFollowingUsersAction(25, 0));
      setLoading(false);
    }
  }, [dispatch, communityName]);

  // Determine initial tab for joined communities: prefer posts if any exist, otherwise members
  useEffect(() => {
    if (!communityData?._id || initialTabResolved || !isMember) return;
    if (!initialPostsRequested) {
      setInitialPostsRequested(true);
      dispatch(getComPostsAction(communityData._id, 1, 0));
    }
  }, [dispatch, communityData?._id, initialPostsRequested, initialTabResolved, isMember]);

  useEffect(() => {
    // If user is not a member, open Members and mark resolved
    if (!isMember && !initialTabResolved) {
      setActiveTab("members");
      setInitialTabResolved(true);
      return;
    }
    if (!initialPostsRequested || initialTabResolved) return;
    // Once a posts fetch has occurred for a member, decide the tab
    setActiveTab(totalCommunityPosts > 0 ? "posts" : "members");
    setInitialTabResolved(true);
  }, [isMember, initialPostsRequested, initialTabResolved, totalCommunityPosts]);

  // Sort members for avatar display: admins first, then followed users, then alphabetically
  const sortedMembersForAvatars = useMemo(() => {
    if (!communityMembers?.members || communityMembers.members.length === 0) return [];

    const members = [...communityMembers.members];
    const followingUserIds = followingUsers?.map(user => user._id) || [];
    
    const admins = members.filter(member => 
      communityData?.moderators?.some(mod => mod._id === member._id || mod === member._id)
    );
    
    const followedMembers = members.filter(member => 
      !admins.some(admin => admin._id === member._id || admin === member._id) &&
      followingUserIds.includes(member._id)
    );
    
    const otherMembers = members.filter(member => 
      !admins.some(admin => admin._id === member._id || admin === member._id) &&
      !followedMembers.some(followed => followed._id === member._id || followed === member._id)
    );

    const sortByName = (a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    };

    return [
      ...admins.sort(sortByName),
      ...followedMembers.sort(sortByName),
      ...otherMembers.sort(sortByName)
    ];
  }, [communityMembers?.members, communityData?.moderators, followingUsers]);

  const handleToggleFollow = async (targetUserId, currentlyFollowing) => {
    if (!targetUserId || targetUserId === authUser?._id) return;
    try {
      if (currentlyFollowing) {
        await dispatch(unfollowUserAction(targetUserId));
      } else {
        await dispatch(followUserAction(targetUserId));
      }
      dispatch(getFollowingUsersAction(25, 0));
    } catch {}
  };

  const handleJoin = async () => {
    if (isJoining) return;
    setIsJoining(true);
    try {
      await dispatch(joinCommunityAction(communityName));
      showSuccess(`Successfully joined ${communityName}!`);
      await dispatch(getJoinedCommunitiesAction());
      await dispatch(getComMembersAction(communityName));
    } catch (error) {
      showError(error.message || "Failed to join community. Please try again.");
      console.error('Error joining community:', error);
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeave = async () => {
    if (isLeaving) return;
    setIsLeaving(true);
    try {
      await dispatch(leaveCommunityAction(communityName));
      showSuccess(`Successfully left ${communityName}!`);
      await dispatch(getJoinedCommunitiesAction());
      await dispatch(getComMembersAction(communityName));
    } catch (error) {
      showError(error.message || "Failed to leave community. Please try again.");
      console.error('Error leaving community:', error);
    } finally {
      setIsLeaving(false);
    }
  };

  if (loading || !communityData || !joinedCommunities) {
    return (
      <div className="flex flex-col lg:flex-row gap-3 max-w-7xl mx-auto px-2">
        <div className="flex-1 flex items-center justify-center h-screen">
          <CommonLoading />
        </div>
      </div>
    );
  }

  const renderContent = () => {
    const currentTab = activeTab;
    
    switch (currentTab) {
      case "posts":
        return <CommunityMainSection />;
      
      case "members":
        return (
          <div className="bg-white border rounded-lg p-4">
            <div className="mb-4 border-b pb-2">
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 font-heading">Members</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
            {sortedMembersForAvatars.map((member) => {
              const isAdmin = communityData.moderators?.some(mod => mod._id === member._id || mod === member._id);
              const followingUserIds = followingUsers?.map(u => u._id) || [];
              const isFollowing = followingUserIds.includes(member._id);
              const isSelf = authUser?._id === member._id;
              
              return (
                <div 
                  key={member._id} 
                  className={`bg-white border rounded-md w-full px-4 py-4 shadow-md hover:shadow-lg hover:border-primary-300 transition-all duration-200 ${isAdmin ? 'border-yellow-400' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/profile/${member.profileUrl || member._id}`)}>
                      <h4 className="font-semibold text-gray-800 font-heading text-sm truncate">
                        {member.name}
                        {isAdmin && <span className="ml-2 text-yellow-600">⭐</span>}
                      </h4>
                      <p className="text-xs text-gray-500 font-roboto-flex truncate">
                        {member.city && member.country ? `${member.city}, ${member.country}` : member.city || member.country || ''}
                      </p>
                    </div>
                    {!isSelf && (
                      <button
                        onClick={() => handleToggleFollow(member._id, isFollowing)}
                        className={`ml-auto p-2 rounded-full transition-colors ${
                          isFollowing
                            ? 'text-action/80 hover:text-action hover:bg-action/10'
                            : 'text-primary-600 hover:text-primary-700 hover:bg-primary-50'
                        }`}
                        title={isFollowing ? 'Unfollow' : 'Follow'}
                        aria-label={isFollowing ? 'Unfollow' : 'Follow'}
                      >
                        {isFollowing ? <FaUserMinus className="w-5 h-5" /> : <FaUserPlus className="w-5 h-5" />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        );
      
      case "about":
        return (
          <div className="bg-white border rounded-lg p-4">
            <div className="mb-4 border-b pb-2">
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 font-heading">About this community</h3>
            </div>
            <div className="space-y-4">
            <div>
              <h4 className="text-base md:text-lg font-semibold text-gray-800 mb-2 font-roboto-flex">Description</h4>
              <div className="text-gray-600 font-roboto-flex">
                <RichTextContent content={communityData.description || 'No description available'} />
              </div>
            </div>
            
            {communityData.rules && communityData.rules.length > 0 && (
              <div>
                <h4 className="text-base md:text-lg font-semibold text-gray-800 mb-2 font-roboto-flex">Rules</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600 font-roboto-flex">
                  {communityData.rules.map((rule, index) => (
                    <li key={index}>{rule}</li>
                  ))}
                </ul>
              </div>
            )}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-2 md:gap-3 max-w-7xl mx-auto px-2 md:px-6">
      {/* Left SidebaFr */}
      <div className="hidden md:block lg:w-60 space-y-2 md:space-y-3">
        {/* Community Info Card */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex flex-col items-center text-center">
            <img
              src={communityData.banner || placeholder}
              alt={communityData.name}
              className="w-20 h-20 rounded-full object-cover mb-3"
            />
            <h2 className="text-lg font-semibold text-gray-800 font-roboto-flex mb-2">
              {communityData.name}
            </h2>
            
            {/* Member Avatars */}
            {sortedMembersForAvatars && sortedMembersForAvatars.length > 0 && (
              <div className="flex items-center justify-center gap-2 mb-2">
                {sortedMembersForAvatars.slice(0, 3).map((member, index) => (
                  <img
                    key={member._id}
                    src={member.avatar}
                    alt={member.name}
                    className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm"
                    style={{ marginLeft: index > 0 ? '-8px' : '0' }}
                  />
                ))}
                {sortedMembersForAvatars.length > 3 && (
                  <div className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white shadow-sm flex items-center justify-center" style={{ marginLeft: '-8px' }}>
                    <span className="text-xs font-semibold text-gray-600">+{sortedMembersForAvatars.length - 3}</span>
                  </div>
                )}
              </div>
            )}
            
            <p className="text-sm text-gray-500 font-roboto-flex mb-3">
              {communityData.members?.length || 0} members
            </p>
            
            {/* Join/Leave Button */}
            {isMember ? (
              <button
                onClick={handleLeave}
                disabled={isLeaving}
                className="w-full bg-action hover:bg-action-hover text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors font-roboto-flex disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <FaSignOutAlt />
                {isLeaving ? "Leaving..." : "Leave Community"}
              </button>
            ) : (
              <button
                onClick={handleJoin}
                disabled={isJoining}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors font-roboto-flex disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <FaUserPlus />
                {isJoining ? "Joining..." : "Join Community"}
              </button>
            )}
          </div>
        </div>

        {/* Navigation Card */}
        <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm">
          <div className="flex flex-col space-y-0">
            {isMember && (
              <button
                onClick={() => setActiveTab("posts")}
                className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${
                  activeTab === "posts"
                    ? "text-primary-700 bg-primary-50"
                    : "text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                }`}
              >
                <FaFileAlt className={`text-base md:text-lg ${
                  activeTab === "posts" ? "text-primary-700" : "text-gray-500"
                }`} />
                <span className="font-roboto-flex text-sm md:text-base">Posts</span>
              </button>
            )}

            <button
              onClick={() => setActiveTab("members")}
              className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${
                activeTab === "members"
                  ? "text-primary-700 bg-primary-50"
                  : "text-gray-700 hover:text-primary-800 hover:bg-gray-50"
              }`}
            >
              <IoMdPeople className={`text-base md:text-lg ${
                activeTab === "members" ? "text-primary-700" : "text-gray-500"
              }`} />
              <span className="font-roboto-flex text-sm md:text-base">Members</span>
            </button>

            <button
              onClick={() => setActiveTab("about")}
              className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${
                activeTab === "about"
                  ? "text-primary-700 bg-primary-50"
                  : "text-gray-700 hover:text-primary-800 hover:bg-gray-50"
              }`}
            >
              <FaInfoCircle className={`text-base md:text-lg ${
                activeTab === "about" ? "text-primary-700" : "text-gray-500"
              }`} />
              <span className="font-roboto-flex text-sm md:text-base">About</span>
            </button>
          </div>
        </div>
      </div>

      {/* Center Content */}
      <div className="flex-1 w-full lg:max-w-6xl min-w-0">
        {renderContent()}
      </div>

      {/* Right Sidebar */}
      <div className="hidden md:block lg:w-60 space-y-2 md:space-y-3">
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <h5 className="mb-4 text-sm font-semibold font-roboto-flex">Content Section</h5>
          <div className="text-center text-gray-500 font-roboto-flex py-8">
            <div className="text-4xl mb-3">🚀</div>
            <p className="text-sm">Coming Soon</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyCommunityDetail;

